import React, { useState } from "react"
import type { EstablishmentData, MunicipalityLookup } from "../../../types"
import { getJSON } from "../../../lib/api"
import { UFS } from "../constants"
import { Field, Input, Select, StepNav } from "../ui"

interface Props {
  data: EstablishmentData
  onChange: (data: EstablishmentData) => void
  onMunicipalityLookup: (lookup: MunicipalityLookup | null) => void
  onBack: () => void
  onContinue: () => void
}

interface ViaCepResponse {
  erro?: boolean
  logradouro?: string
  localidade?: string
  uf?: string
}

export default function EstablishmentStep({ data, onChange, onMunicipalityLookup, onBack, onContinue }: Props) {
  const [touched, setTouched] = useState(false)
  const [cepLoading, setCepLoading] = useState(false)

  const set = (field: keyof EstablishmentData) => (value: string) =>
    onChange({ ...data, [field]: value })

  // ViaCEP: preenche endereço a partir do CEP (grátis, sem chave)
  async function fillFromCep(rawCep: string) {
    const cep = rawCep.replace(/\D/g, "")
    if (cep.length !== 8) return
    setCepLoading(true)
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`)
      const body: ViaCepResponse = await response.json()
      if (!body.erro) {
        onChange({
          ...data,
          cep: rawCep,
          logradouro: data.logradouro || body.logradouro || "",
          municipio: body.localidade || data.municipio,
          uf: body.uf || data.uf
        })
      }
    } catch {
      // offline/erro: usuário preenche manualmente
    } finally {
      setCepLoading(false)
    }
  }

  const valid = data.fantasy_name.trim() !== "" && data.municipio.trim() !== "" && data.uf !== ""

  async function continueStep() {
    setTouched(true)
    if (!valid) return
    // Camada municipal: dispara o disclaimer de ART antes do pagamento
    try {
      const lookup = await getJSON<MunicipalityLookup>(
        `/api/municipalities/lookup?municipio=${encodeURIComponent(data.municipio)}&uf=${encodeURIComponent(data.uf)}`
      )
      onMunicipalityLookup(lookup)
    } catch {
      onMunicipalityLookup(null)
    }
    onContinue()
  }

  return (
    <section>
      <h1 className="font-display text-3xl sm:text-4xl font-semibold text-ink">Sobre o estabelecimento</h1>
      <p className="mt-3 text-gray-600">Esses dados identificam o gerador de resíduos no documento.</p>

      <div className="mt-8 space-y-5">
        <Field label="Nome fantasia" required error={touched && data.fantasy_name.trim() === "" ? "Obrigatório" : null}>
          <Input value={data.fantasy_name} onChange={set("fantasy_name")} placeholder="Ex.: Clínica Sorriso" autoFocus />
        </Field>

        <Field label="CNPJ" hint="Opcional — dá pra adicionar depois">
          <Input value={data.cnpj} onChange={set("cnpj")} placeholder="00.000.000/0000-00" inputMode="numeric" />
        </Field>

        <div className="grid grid-cols-[9rem_1fr] gap-4">
          <Field label="CEP" hint={cepLoading ? "Buscando…" : undefined}>
            <Input
              value={data.cep}
              onChange={set("cep")}
              onBlur={() => fillFromCep(data.cep)}
              placeholder="00000-000"
              inputMode="numeric"
            />
          </Field>
          <Field label="Endereço">
            <Input value={data.logradouro} onChange={set("logradouro")} placeholder="Rua, número" />
          </Field>
        </div>

        <div className="grid grid-cols-[1fr_7rem] gap-4">
          <Field label="Município" required error={touched && data.municipio.trim() === "" ? "Obrigatório" : null}>
            <Input value={data.municipio} onChange={set("municipio")} placeholder="Ex.: São Paulo" />
          </Field>
          <Field label="UF" required error={touched && data.uf === "" ? "Obrigatório" : null}>
            <Select value={data.uf} onChange={set("uf")} options={[...UFS]} placeholder="UF" />
          </Field>
        </div>

        <Field label="Porte" hint="Opcional">
          <Select
            value={data.porte}
            onChange={set("porte")}
            options={["pequeno", "medio", "grande"]}
            labels={{ pequeno: "Pequeno (até 10 pessoas)", medio: "Médio (11 a 50)", grande: "Grande (50+)" }}
            placeholder="Selecione"
          />
        </Field>
      </div>

      <StepNav onBack={onBack} onContinue={continueStep} continueDisabled={touched && !valid} />
    </section>
  )
}
